import { z } from "zod/v4";
import { defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { stringToInt } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../feature-name";

/**
 * Patriot clients — Patriot's accounts for the premises it monitors, as the
 * replica holds them.
 *
 * Fleet-wide only. A client does not map cleanly onto an a3s tenant, site or
 * camera (one client can cover several cameras, and client numbers are reused
 * over time), so there is no tenant-scoped view to narrow it to.
 *
 * Like the activation shapes, these are an **allowlist** of what support needs
 * to work an operational issue: who the client is, how to reach the premises,
 * what the operator is told to do, and how Patriot groups it. Left out:
 * `externalRefNo`, a Patriot-internal surrogate that carries no a3s meaning.
 *
 * **Patriot's fields keep Patriot's names**; a3s-owned fields are
 * `a3s`-prefixed, as in `activations.ts`.
 */

/** One row of the client list. */
export const ClientSummary = z.object({
  /** Patriot's client number, e.g. `10539BASE01`. */
  clientNo: z.string(),
  name: z.string(),
  /** The premises' suburb or town. */
  city: z.string().optional(),
  state: z.string().optional(),
  /** Whether Patriot has operators attend this client's alarms. */
  attendAlarm: z.boolean().optional(),
});
export type ClientSummary = z.output<typeof ClientSummary>;

export const ListFleetClientsResponse = z.object({
  items: z.array(ClientSummary),
  /** Opaque resume token; `null` when the list is exhausted. Pass back verbatim. */
  cursor: z.string().nullable(),
});
export type ListFleetClientsResponse = z.output<typeof ListFleetClientsResponse>;

/**
 * `clientNo` finds clients whose number starts with it, ignoring case — the
 * asset number support knows a client by (`11238` for `11238BASE01`). That is
 * what the replica's client index answers as a key range; searching within a
 * number or by Patriot's hand-typed name is not offered.
 *
 * Absent `clientNo`, the list is every client, in client-number order.
 */
export const ListFleetClientsQueryParams = z.object({
  limit: stringToInt.pipe(z.int().positive().max(100)).default(25),
  cursor: z.string().optional(),
  clientNo: z.string().trim().min(1).optional(),
});
export type ListFleetClientsQueryParamsWire = z.input<typeof ListFleetClientsQueryParams>;
export type ListFleetClientsQueryParams = z.output<typeof ListFleetClientsQueryParams>;

/**
 * Under the caller's tenant in the path only because every client-API route is;
 * the tenant does not narrow the list. See `fleetActivationsResource`.
 */
export const fleetClientsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/clients",
  scope: "tenant",
});

export const listFleetClientsEndpoint = defineEndpoint(fleetClientsResource, {
  responseSchema: ListFleetClientsResponse,
  querySchema: ListFleetClientsQueryParams,
});

export const ClientAddress = z.object({
  addressLines: z.array(z.string()),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  postcode: z.string().optional(),
});
export type ClientAddress = z.output<typeof ClientAddress>;

/**
 * One grouping a client is tagged with — e.g. type `Vision Intelligence`,
 * name `Brisbane`.
 *
 * Named plainly rather than with Patriot's names (`clientGroupingID`,
 * `description`, `groupType`), unlike the rest of this file: Patriot calls a
 * grouping's name its `description`, which read as something else entirely.
 *
 * `name` is absent when the replica holds the client's reference but has not
 * copied the grouping itself yet, and likewise for the type.
 */
export const ClientGroupingSummary = z.object({
  id: z.string(),
  type: z
    .object({
      id: z.string(),
      name: z.string().optional(),
      /** Whether a client can hold more than one grouping of this type. */
      allowMultiple: z.boolean().optional(),
    })
    .optional(),
  name: z.string().optional(),
});
export type ClientGroupingSummary = z.output<typeof ClientGroupingSummary>;

/** An action plan a client's configuration names, resolved from the replica's own action plan record. */
export const ClientActionPlan = z.object({
  actionPlanId: z.string(),
  /** Absent when the replica has not copied this action plan yet. */
  description: z.string().optional(),
  /** Patriot priority; lower is more urgent. */
  priority: z.number().optional(),
});
export type ClientActionPlan = z.output<typeof ClientActionPlan>;

/**
 * One event type — a signal `typeNo` — and the action plan Patriot runs for it
 * at this client. `definedBy` is the nearest client in the template chain that
 * defines it (this client, for its own overrides); `replaces` is the next-nearest
 * definition it hides — the general plan behind an override.
 */
export const ClientEventType = z.object({
  typeNo: z.number(),
  description: z.string().optional(),
  /** Patriot's `ActionPlanDescriptor`; `USE_ACTION_PLAN` on almost every event type. */
  signalType: z.string().optional(),
  actionPlan: ClientActionPlan.optional(),
  /** Event-type-specific operator instructions. Rarely set. */
  instructions: z.string().optional(),
  definedBy: z.string(),
  replaces: z
    .object({
      definedBy: z.string(),
      actionPlan: ClientActionPlan.optional(),
    })
    .optional(),
});
export type ClientEventType = z.output<typeof ClientEventType>;

/**
 * A client-wide swap of one action plan for another — wherever `actionPlan`
 * would run, `alternativeActionPlan` runs instead — optionally limited to an
 * action plan grouping.
 */
export const ClientActionPlanOverride = z.object({
  actionPlanOverrideId: z.number(),
  actionPlan: ClientActionPlan.optional(),
  alternativeActionPlan: ClientActionPlan.optional(),
  actionPlanGrouping: z.string().optional(),
});
export type ClientActionPlanOverride = z.output<typeof ClientActionPlanOverride>;

/** Client-specific operator instructions for one action plan. `override` replaces the plan's own instructions rather than adding to them. */
export const ClientActionPlanInstructions = z.object({
  actionPlan: ClientActionPlan.optional(),
  override: z.boolean().optional(),
  instructions: z.string().optional(),
});
export type ClientActionPlanInstructions = z.output<typeof ClientActionPlanInstructions>;

/**
 * Where a client's event types come from: the template chain, nearest first.
 * `name` is absent for a template the replica has not copied, which also ends
 * the chain there — the event types it would have contributed are missing.
 */
export const ClientTemplate = z.object({
  clientNo: z.string(),
  name: z.string().optional(),
});
export type ClientTemplate = z.output<typeof ClientTemplate>;

/**
 * One client in full.
 *
 * `responseInstructions` is free text written by Vision staff for the
 * monitoring operator, carried verbatim — it often holds camera IPs and site
 * access notes.
 *
 * `a3sFirstSeenAt` / `a3sLastWrittenAt` are when the replica first copied this
 * client and last copied a change to it — how fresh this view is, not anything
 * Patriot recorded.
 */
export const ClientDetail = ClientSummary.extend({
  description: z.string().optional(),
  email: z.string().optional(),
  sitePhone: z.string().optional(),
  siteMobile: z.string().optional(),
  address: ClientAddress.optional(),
  responseInstructions: z.string().optional(),
  clientGroupings: z.array(ClientGroupingSummary),
  /** Whether this client is a template other clients inherit event types from. */
  isTemplate: z.boolean().optional(),
  eventTypesTemplates: z.array(ClientTemplate),
  /** The event types in effect at this client, inherited and own, by `typeNo`. */
  eventTypes: z.array(ClientEventType),
  actionPlanOverrides: z.array(ClientActionPlanOverride),
  actionPlanInstructions: z.array(ClientActionPlanInstructions),
  a3sFirstSeenAt: z.string().optional(),
  a3sLastWrittenAt: z.string().optional(),
});
export type ClientDetail = z.output<typeof ClientDetail>;

export const GetClientPathParams = z.object({ clientNo: z.string().min(1) });
export type GetClientPathParams = z.output<typeof GetClientPathParams>;

export const fleetClientResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/clients/{clientNo}",
  scope: "tenant",
  resourcePathSchema: GetClientPathParams,
});

export const getFleetClientEndpoint = defineEndpoint(fleetClientResource, {
  responseSchema: ClientDetail,
});
